<template>
  <div
    id="site"
    class="site"
    :class="[
      theme,
      { 'navigation-hidden': !navigation },
      { empty: empty },
      { 'row-gap': rowGap },
      { 'gutters-mobile-content-off': !contentMobileGutters },
      { 'banner-sticky-on-mobile': bannerStickyOnMobile },
      types,
    ]"
  >
    <header v-if="!empty" class="site-header site-gutters">
      <AppHeaderBonusProgress class="site-header-bonus-progress" />

      <div class="site-gutters-content site-header-content">
        <KeepAlive>
          <AppHeader :navigation="navigation" />
        </KeepAlive>
      </div>
    </header>

    <div class="site-main site-gutters">
      <div class="site-gutters-content site-main-content">
        <slot name="main" />
      </div>
    </div>

    <div class="banner"><slot name="banner" /></div>

    <footer v-if="!empty" class="site-footer site-gutters">
      <div class="site-gutters-content site-footer-content">
        <KeepAlive>
          <AppFooter v-if="navigation" />
        </KeepAlive>
      </div>
    </footer>
  </div>

  <slot name="modal" />
  <div class="infos">v {{ runtimeConfig.public.version }}</div>
</template>

<script lang="ts" setup>
interface Props {
  types?: string[];
  theme?: string;
  navigation?: boolean;
  empty?: boolean;
  rowGap?: boolean;
  contentMobileGutters?: boolean;
  bannerStickyOnMobile?: boolean;
}

defineProps({
  types: { default: () => ['default'] },
  theme: { default: 'default' },
  navigation: { type: Boolean, default: true },
  empty: { type: Boolean, default: false },
  rowGap: { type: Boolean, default: true },
  contentMobileGutters: { type: Boolean, default: true },
  bannerStickyOnMobile: { type: Boolean, default: false }
});

const runtimeConfig = useRuntimeConfig();
</script>
<style lang="scss" scoped>
.site {
  display: grid;
  grid-template-areas: 'header' 'main' 'banner' 'footer';
  grid-template-rows: min-content 1fr auto var(--footer_height);
  &.row-gap {
    grid-template-areas: 'header' '.' 'main' '.' 'banner' 'footer';
    grid-template-rows: min-content var(--row_gap) 1fr var(--row_gap) auto var(
        --footer_height
      );
  }

  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  column-gap: minmax(0, var(--column_gap));

  &.empty {
    grid-template-areas: 'main' 'banner';
    grid-template-rows: 1fr auto;
    &-header {
      display: none;
    }
    &-footer {
      display: none;
    }
    &-main {
      min-height: calc(
        100vh - var(--header_height) - var(--footer_height) - 2 * var(--row_gap)
      );
      min-height: calc(
        100dvh - var(--header_height) - var(--footer_height) - 2 *
          var(--row_gap)
      );
    }
  }

  @include mobile {
    .disable-navigation-mobile & {
      grid-template-areas: 'main';
      grid-template-columns: 1fr;
      grid-template-rows: 1fr;

      header {
        display: none;
      }
    }
  }

  &.adventure {
    background-color: var(--color_neutral_25);
  }

  &-gutters {
    display: grid;
    grid-template-areas: 'content';
    grid-template-columns: 1fr;
    width: stretch;
    @include above('l') {
      grid-template-areas: '. content .';
      grid-template-columns:
        1fr
        calc(var(--main_width))
        1fr;
    }
    &-content {
      grid-area: content;
      width: 100%;
      padding: 0;
      @include above('l') {
        padding: 0;
      }
    }
  }

  &-header {
    position: sticky;
    z-index: 3;
    top: 0;
    grid-area: header;
    height: var(--header_height);
    background: var(--color_neutral_25);
    box-shadow: rgb(96 72 253 / 5%) 1.95px 1.95px 2.6px;
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: var(--stroke_radius_xl) var(--stroke_radius_xl) 0 0;
      background-color: var(--color_neutral_25);
    }

    &-content {
      position: relative;
      z-index: 1;
      padding: 0 var(--gap_n);
    }
  }

  &-main {
    grid-area: main;

    &-content {
      padding: 0 var(--gap_n);
      .gutters-mobile-content-off & {
        padding: 0;
      }
    }
  }

  &-footer {
    position: relative;
    position: sticky;
    grid-area: footer;
    background-color: var(--color_neutral_25);
    &::before {
      content: '';
      position: absolute;
      top: -0.1rem;
      width: 100%;
      height: 0.1rem;
      opacity: 1;
      background-color: var(--color_secondary_light);
    }

    @include mobile {
      z-index: 5;
      bottom: 0;
    }
    @include above('l') {
      display: none;
    }
  }
}

.banner {
  z-index: 2;
  bottom: var(--footer_height);
  grid-area: banner;
  width: 100%;
  @include above('l') {
    position: sticky;
    bottom: 0;
  }
  .banner-sticky-on-mobile & {
    position: sticky;
    bottom: 0;
  }
}

.infos {
  position: fixed;
  z-index: 6;
  right: 0.2rem;
  bottom: 0;
  padding: 0.1rem;
  transform-origin: 100% 0%;
  border-radius: 0.2rem;
  color: var(--color_neutral_300);
  @include font('xxs', 500);
  @include above('l') {
    background-color: rgb(0 0 0 / 10%);
    color: var(--color_neutral_300);
  }
}
</style>
